import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import UserAvatar from "@/components/UserAvatar";
import UserProfileDialog from "@/components/UserProfileDialog";
import { MessageCircle, Star } from "lucide-react";

function initial(name) {
  const s = (name || "?").trim();
  return s.charAt(0).toUpperCase();
}

/**
 * Compact inbox card showing all peer-to-peer DM conversations for the current user.
 * Each row opens a `UserProfileDialog` directly on the DM thread.
 */
export default function UserDmInbox() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPeerId, setOpenPeerId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/user-dm-inbox");
      setConversations(data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-dm-inbox">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#00b4ff]/10 grid place-items-center text-[#00b4ff]">
          <MessageCircle size={16} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Private messages</div>
          <div className="font-display font-semibold text-lg">Inbox</div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="text-slate-400 text-sm font-mono-plex text-xs">
            No private conversations yet. Tap a name in Community Chat to start one.
          </div>
        ) : conversations.map((c) => (
          <button
            key={c.peer.id}
            data-testid={`inbox-row-${c.peer.id}`}
            onClick={() => setOpenPeerId(c.peer.id)}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-[#00b4ff] hover:bg-slate-50 text-left"
            style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
          >
            <UserAvatar
              path={c.peer.profile_picture_path}
              size={40}
              className={`${c.peer.is_master ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white" : "bg-slate-200 text-slate-700"} text-sm font-bold`}
              fallback={c.peer.is_master ? <Star size={14} className="fill-white" /> : initial(c.peer.name || c.peer.email)}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display font-semibold text-sm truncate">{c.peer.name || (c.peer.email || "").split("@")[0]}</div>
                {c.peer.is_master && <span className="text-[8px] font-mono-plex tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">MASTER</span>}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message.text}</div>
            </div>
            {c.unread > 0 && (
              <span className="shrink-0 min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-mono-plex font-bold grid place-items-center">
                {c.unread > 9 ? "9+" : c.unread}
              </span>
            )}
          </button>
        ))}
      </div>
      <UserProfileDialog
        userId={openPeerId}
        open={!!openPeerId}
        onOpenChange={(o) => { if (!o) { setOpenPeerId(null); load(); } }}
        initialMode="dm"
      />
    </div>
  );
}
